import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { SidePanelContent } from "@components/sidepanel/side-panel-content";
import { useRemoveMemberBulkDialogContent } from "@components/table/members/bulk-actions/bulk-remove-member-helper";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { TeamMembersTable } from "@components/table/members/team-members-table";
import { mapTeamMembersToSdbMembers } from "@components/table/teams/teams-table-utils";
import { BaseTeamProps, SdbTeamMemberBase } from "@custom-types/teams-types";
import { useMembers } from "@hooks/use-members";
import { Stack } from "@mui/material";
import { InviteMemberToTeam } from "@pages/members/teams/invite-member-to-team";
import { TeamDetailsInfoBar } from "@pages/members/teams/team-details/team-details-info-bar";
import { TeamMembersPageBulkActions } from "@pages/members/teams/team-members-page-bulk-actions";
import { TeamSidePanelDescription } from "@pages/members/teams/team-side-panel-description";
import { NotFoundPage } from "@pages/not-found-page";
import { useAppParams } from "@router/router-helper";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import {
  fetchingTeamsFlagsSelector,
  isDetailOpenedSelector,
  nextTeamMemberSelector,
  selectedTeamMembersSelector,
  teamWithRoleByIdSelector,
} from "@store/teams/teams-selector";
import { fetchTeamMembers } from "@store/teams/teams-slice-thunk";
import { searchSelector } from "@store/ui/ui-selector";
import { getMembersMapById } from "@utils/member-utils";
import { useCallback, useEffect, useMemo } from "react";

/** Side panel width in pixel */
const SIDE_PANEL_WIDTH = "320";

/**
 * Contains the Members tab of the team details
 */
export function TeamMembers({ team }: Partial<BaseTeamProps>): JSX.Element {
  const dispatch = useAppDispatch();
  const { companyId } = useAppParams();
  const teamDetails = useAppSelector(teamWithRoleByIdSelector(team?.id));
  const teamMembers = useAppSelector(selectedTeamMembersSelector);
  const { isFetchingTeamMembers, isFetchingMoreMembers } = useAppSelector(
    fetchingTeamsFlagsSelector
  );
  const { companyMembers } = useMembers();
  const membersMap = getMembersMapById(companyMembers);

  const isDetailOpened = useAppSelector(isDetailOpenedSelector);
  const { isDebouncingSearch } = useAppSelector(searchSelector);

  /** Stores the id of the next team member to be fetched */
  const nextTeamMember = useAppSelector(nextTeamMemberSelector);

  const { successDialogContent } = useRemoveMemberBulkDialogContent("team");

  /**
   * Decides whether to show the empty page or not.
   * - True if there is no members in the team
   */
  const shouldShowEmptyPage =
    isFetchingTeamMembers === "succeeded" && teamMembers.length === 0;

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );

  /**
   * Compute loading state based on multiple factors
   */
  const isLoading = useMemo(() => {
    return (
      isFetchingTeamMembers === "pending" ||
      isFetchingMoreMembers ||
      isDebouncingSearch
    );
  }, [isFetchingTeamMembers, isFetchingMoreMembers, isDebouncingSearch]);

  // Fetch team members on component mount
  useEffect(() => {
    if (companyId && team) {
      void dispatch(
        fetchTeamMembers({
          companyId,
          teamId: team.id,
          start: undefined,
        })
      );
    }
  }, [companyId, dispatch, team]);

  // Constructing the members list for members table from sample members of a team
  const sbdTeamMembers: SdbTeamMemberBase[] = useMemo(() => {
    if (!teamMembers.length || !teamDetails) {
      return [];
    }

    return mapTeamMembersToSdbMembers(
      teamMembers,
      teamDetails.role,
      membersMap
    );
  }, [membersMap, teamDetails, teamMembers]);

  /**
   * Load more team members with pagination
   */
  const loadMoreMembers = useCallback(async (): Promise<void> => {
    if (companyId && team?.id && nextTeamMember) {
      await dispatch(
        fetchTeamMembers({
          companyId,
          teamId: team.id,
          start: nextTeamMember,
        })
      );
    }
  }, [companyId, dispatch, nextTeamMember, team]);

  if (!companyId || !team) {
    return <NotFoundPage />;
  }

  const buttonComponents = TeamMembersPageBulkActions({
    companyId,
    team,
    teamMembers: sbdTeamMembers,
  });

  return (
    <>
      {bulkActionName === "removeMember" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      <TeamDetailsInfoBar isLoading={isLoading} />

      {team && shouldShowEmptyPage ? (
        <Stack flexDirection="row" gap={isDetailOpened ? "20px" : 0}>
          <MembersEmptyPage
            subtitle={`All the members of your ${TEAM_DISPLAY_NAME} will be found here. You can add members to your ${TEAM_DISPLAY_NAME} by inviting them.`}
            button={<InviteMemberToTeam team={team} buttonVariant="filled" />}
          />

          <SidePanelContent
            isOpened={isDetailOpened}
            contentWidth={SIDE_PANEL_WIDTH}
            sx={{ mt: "14px" }}
          >
            <TeamSidePanelDescription team={team} />
          </SidePanelContent>
        </Stack>
      ) : (
        <TeamMembersTable
          companyId={companyId}
          members={sbdTeamMembers}
          team={team}
          isLoading={isLoading}
          requiredColumns={[
            MemberHeaders.avatar,
            MemberHeaders.user,
            MemberHeaders.email,
            MemberHeaders.status,
            MemberHeaders.options,
          ]}
          bulkActionButtons={buttonComponents}
          sidePanelContent={<TeamSidePanelDescription team={team} />}
          sidePanelContentWidth={SIDE_PANEL_WIDTH}
          isSidePanelOpened={isDetailOpened}
          loadMoreItems={loadMoreMembers}
          hasMoreItems={!!nextTeamMember}
        />
      )}
    </>
  );
}
